import type { Page as ApiPage } from '@arhs-fleetback/types/entities/common';

import { PaginationType } from 'constants/Global';
import type { Page } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';

type SliceApiPage = Extract<ApiPage, { type: typeof PaginationType.SLICE }>;

/**
 * @param {any} payload
 * @returns {any}
 */
export const cleanPayload = (payload: any): any => {
  if (Array.isArray(payload)) {
    return payload.map(cleanPayload);
  }
  if (typeof payload === 'object' && payload !== null) {
    return Object.entries(payload).reduce(
      (acc: any, [key, value]) => ({
        ...acc,
        [key]: cleanPayload(value)
      }),
      {}
    );
  }
  if (typeof payload === 'string' && payload.trim() === '') {
    return undefined;
  }
  return payload;
};

/**
 * Turn the SLICE block the API returns into the counted page the store expects: a slice gives up
 * the totals, so a full page of rows reports Infinity to mean "there is more".
 */
const buildSlicePageObject = (page: SliceApiPage, nbLoadedItems: number): Page => {
  const { size, number } = page;
  return {
    ...page,
    totalPages: nbLoadedItems === size ? Infinity : number,
    totalElements: nbLoadedItems === size ? Infinity : number * size + nbLoadedItems
  };
};

/**
 * Resolve the slice the response describes. The API states it in the `page` block; a response that
 * carries no block at all is answered on the strength of the request, which is what every stubbed
 * list in the Cypress suite relies on to receive a page at all.
 */
const getSlicePage = (response: any): MayBe<SliceApiPage> => {
  const apiPage: MayBe<ApiPage> = response.data?.page;

  if (apiPage) {
    return apiPage.type === PaginationType.SLICE ? apiPage : undefined;
  }

  const { paginationType, page: number = 0, size } = response.config?.params ?? {};
  return paginationType === PaginationType.SLICE
    ? { number, size, type: PaginationType.SLICE }
    : undefined;
};

export const sliceResponseFormatter = (response: any) => {
  const slicePage = getSlicePage(response);

  if (slicePage) {
    const { page: _, ...rest } = response.data;
    response.data = { page: buildSlicePageObject(slicePage, response.data.content.length), ...rest };
  }
  return response;
};

export const excludeFilter = (data: any, filter: any) => {
  const excludedFilter = Array.isArray(filter) ? filter : [filter];
  const allData = Array.isArray(data) ? data : Object.values(data ?? {});
  return allData.filter(elem => !excludedFilter.includes(elem));
};

/**
 * @returns {{ 'X-Fleetback-Origin': string, 'X-Fleetback-Path': string }}
 */
export const getDefaultHeaders = () => ({
  'X-Fleetback-Origin': 'BO',
  'X-Fleetback-Path': window.location.pathname + window.location.search
});

type JsonPayload = string | number | boolean | null | undefined | JsonPayload[] | { [key: string]: JsonPayload };

const isEmptyValue = (value: JsonPayload): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return false;
};

export const deepCleanPayload = (payload: JsonPayload): JsonPayload => {
  if (Array.isArray(payload)) {
    const cleaned = payload.map(deepCleanPayload).filter(item => !isEmptyValue(item));
    return cleaned.length === 0 ? undefined : cleaned;
  }

  if (typeof payload === 'object' && payload !== null) {
    const entries = Object.entries(payload);
    const cleaned = entries.reduce<Record<string, JsonPayload>>((acc, [key, value]) => {
      const cleanedValue = deepCleanPayload(value);
      if (!isEmptyValue(cleanedValue)) {
        acc[key] = cleanedValue;
      }
      return acc;
    }, {});
    return Object.keys(cleaned).length === 0 ? undefined : cleaned;
  }

  if (typeof payload === 'string' && payload.trim() === '') {
    return undefined;
  }
  return payload;
};
